import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu, LayoutGrid, DollarSign, Users, Settings, Plus, List, 
  ChevronDown, LogOut as LogoutIcon, FileText, Briefcase, 
  Edit, Video, FolderKanban, MessageSquare, UserPlus, Shield
} from "lucide-react";
import logo from "../../assets/logo.png";

export default function MainNavigation({ isSidebarOpen, setIsSidebarOpen }) {
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const closeSidebar = () => {
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
  };

  const navItems = [
    { label: "Dashboard", icon: <LayoutGrid size={18} />, path: "/" },

    // SECTION: CLIENTS & OPERATIONS
    { type: "section", label: "Operations" },
    {
      label: "Projects & Portfolios",
      icon: <FolderKanban size={18} />,
      subItems: [
        { label: "Manage Portfolios", path: "/portfolios", icon: <List size={14} /> },
        { label: "Add New Project", path: "/portfolios/create", icon: <Plus size={14} /> },
      ],
    },
    {
      label: "Leads & Inquiries",
      icon: <MessageSquare size={18} />,
      subItems: [
        { label: "Client Inquiries", path: "/inquiries", icon: <List size={14} /> },
      ],
    },

    // SECTION: CONTENT & BRANDING
    { type: "section", label: "Content & Marketing" },
    {
      label: "Content Studio",
      icon: <FileText size={18} />,
      subItems: [
        { label: "Blog Posts Hub", path: "/blogs", icon: <List size={14} /> },
        { label: "Create Blog Post", path: "/create-blog", icon: <Edit size={14} /> },
        { label: "YouTube Showcase", path: "/youtube-post", icon: <Video size={14} /> },
        { label: "Client Testimonials", path: "/CustomerTestimonials", icon: <MessageSquare size={14} /> },
      ],
    },

    // SECTION: HUMAN RESOURCES & ACCOUNTS
    { type: "section", label: "Organization" },
    {
      label: "Talent & Recruitment",
      icon: <Users size={18} />,
      subItems: [
        { label: "Recruitment Portal", path: "/recruitment", icon: <Briefcase size={14} /> },
        { label: "Team Members", path: "/team-members", icon: <UserPlus size={14} /> },
      ],
    },
    {
      label: "Finance & Accounts",
      icon: <DollarSign size={18} />,
      subItems: [
        { label: "Financial Ledger", path: "/LedgerDashboard", icon: <LayoutGrid size={14} /> },
        { label: "Accounts Management", path: "/CreateAccount", icon: <Plus size={14} /> },
      ],
    },

    // SECTION: CONFIGURATION
    { type: "section", label: "System Administration" },
    {
      label: "Access Control",
      icon: <Settings size={18} />,
      subItems: [
        { label: "Role & Permissions", path: "/Role", icon: <Shield size={14} /> },
        { label: "Create Admin User", path: "/AdminSignup", icon: <UserPlus size={14} /> },
      ],
    },
  ];

  const isParentActive = (item) => {
    if (item.path && location.pathname === item.path) return true;
    return item.subItems?.some((sub) => location.pathname === sub.path);
  };

  useEffect(() => {
    const activeIndex = navItems.findIndex(item => item.type !== "section" && isParentActive(item));
    if (activeIndex !== -1) setOpenMenu(activeIndex);
  }, [location.pathname]);

  return (
    <>
      {/* Top Header Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-[70] bg-white h-16 px-6 flex justify-between items-center border-b border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-10 w-auto">
            <img 
              src={logo} 
              alt="Indiv TechnoSoft Logo" 
              className="h-9 w-auto object-contain"
            />
          </div>
          <div className="h-6 w-[1px] bg-slate-200 mx-1 hidden sm:block"></div>
          <div>
            <h1 className="text-[#194393] text-base font-black tracking-tight leading-none">INDIV TECHNOSOFT</h1>
            <p className="text-[10px] text-[#FF9E1B] font-bold tracking-wider uppercase mt-0.5">Admin Workspace</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:flex flex-col items-end mr-2">
            <span className="text-[11px] text-[#FF9E1B] font-bold bg-amber-50 border border-amber-100 px-2.5 py-0.5 rounded-full uppercase tracking-wide">
              Secure System Node
            </span>
          </div>
          <button 
            onClick={toggleSidebar} 
            className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg transition-all border border-slate-200"
          >
            <Menu size={18} />
          </button>
        </div>
      </nav>

      {/* Main Sidebar */}
      <aside
        className={`fixed top-16 left-0 w-64 bg-slate-50 h-[calc(100vh-64px)] text-slate-600 z-[60] transition-all duration-300 flex flex-col border-r border-slate-200/80
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
      >
        <nav className="p-4 overflow-y-auto custom-scrollbar flex-grow pb-24">
          <ul className="space-y-1">
            {navItems.map((item, index) => {
              if (item.type === "section") {
                return (
                  <li key={`sec-${index}`} className="pt-4 pb-1 first:pt-1">
                    <div className="flex items-center gap-2 px-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                      <div className="h-[1px] w-3 bg-slate-200" />
                      {item.label}
                    </div>
                  </li>
                );
              }

              const isActive = isParentActive(item);
              const isOpen = openMenu === index;

              return (
                <li key={index}>
                  {item.subItems ? (
                    <div
                      className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-all ${isActive ? "bg-white text-[#194393] font-bold shadow-sm border border-slate-200/50" : "hover:bg-slate-200/50 text-slate-600 hover:text-slate-900"}`}
                      onClick={() => setOpenMenu(isOpen ? null : index)}
                    >
                      <div className="flex items-center gap-3">
                        <span className={isActive ? "text-[#194393]" : "text-slate-400"}>{item.icon}</span>
                        <span className="text-sm font-semibold tracking-tight">{item.label}</span>
                      </div>
                      <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                  ) : (
                    <Link
                      to={item.path || "#"}
                      className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${isActive ? "bg-amber-50/60 text-[#194393] font-bold border border-amber-100" : "hover:bg-slate-200/50 text-slate-600"}`}
                      onClick={closeSidebar}
                    >
                      <span className={isActive ? "text-[#FF9E1B]" : "text-slate-400"}>{item.icon}</span>
                      <span className="text-sm font-semibold tracking-tight">{item.label}</span>
                    </Link>
                  )}

                  {/* Accordion Submenus */}
                  <AnimatePresence>
                    {isOpen && item.subItems && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-200 pl-3"
                      >
                        {item.subItems.map((sub, i) => {
                          const isSubActive = location.pathname === sub.path;
                          return (
                            <li key={i}>
                              <Link
                                to={sub.path}
                                className={`flex items-center gap-2 py-2 px-2 rounded text-xs transition-all ${isSubActive ? "text-[#194393] font-bold bg-blue-50/50" : "text-slate-500 hover:text-slate-900 hover:bg-slate-200/30"}`}
                                onClick={closeSidebar}
                              >
                                <span className={isSubActive ? "text-[#194393]" : "text-slate-400"}>{sub.icon}</span>
                                {sub.label}
                              </Link>
                            </li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Terminate Session Footer */}
        <div className="p-4 bg-white border-t border-slate-200 sticky bottom-0">
          <button
            onClick={() => { localStorage.removeItem("admin-auth"); window.location.reload(); }}
            className="w-full flex items-center justify-center gap-2 p-2.5 bg-slate-50 hover:bg-rose-50 text-slate-600 hover:text-rose-600 border border-slate-200 rounded-lg transition-all text-xs font-bold"
          >
            <LogoutIcon size={14} /> Terminate Session
          </button>
        </div>
      </aside>

      {/* Dynamic Layout Offset */}
      <style dangerouslySetInnerHTML={{ __html: `
        main {
          transition: padding-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @media (min-width: 1024px) {
          main {
            padding-left: ${isSidebarOpen ? '256px' : '0px'} !important;
          }
        }
      `}} />
    </>
  );
}